import React, { useEffect, useState } from 'react';
import type { User, PathwayBlock, LevelReviewEntry } from '../../types';
import { DataService } from '../../services/firebase';
import { useTranslation } from 'react-i18next';
import { PathwayProgressFlow } from '../../components/PathwayProgressFlow';
import type { Pathway } from '../../types';

interface PathwayViewProps {
  user: User;
  pathwayId: string;
  selectedTrack?: string;
  onSelectBlock: (block: PathwayBlock) => void;
}

export const PathwayView: React.FC<PathwayViewProps> = ({ user, pathwayId, selectedTrack, onSelectBlock }) => {
  const { t } = useTranslation();
  const [pathway, setPathway] = useState<Pathway | null>(null);
  const [blockProgressMap, setBlockProgressMap] = useState<Map<string, string>>(new Map());
  const [levelReviews, setLevelReviews] = useState<Record<string, LevelReviewEntry>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const pw = await DataService.getPathwayByRootId(pathwayId);
        if (!pw) return;
        setPathway(pw);

        const blockProgressList = await DataService.getBlockProgressForPathway(user.id, pathwayId);
        const map = new Map<string, string>();
        (blockProgressList as any[]).forEach((p: any) => map.set(p.blockId, p.status));
        setBlockProgressMap(map);

        const assignments = await DataService.getPathwaysForCandidate(user.id);
        const assignment = assignments.find(a => a.pathwayId === pathwayId);
        setLevelReviews(assignment?.levelReviews || {});

        // Sync pathway_assignments with real block completion data
        // Filter blocks to the assigned track (same logic as PathwayProgressFlow)
        const allBlocks = pw.blocks || [];
        const visibleBlocks = selectedTrack
          ? allBlocks.filter(b => !b.branchId || b.branchId === selectedTrack || b.branchId === 'MERGE' || b.branchId === 'REMEDIAL')
          : allBlocks;
        const completeCount = visibleBlocks.filter(b => map.get(b.id) === 'complete').length;
        const progress = visibleBlocks.length > 0 ? Math.round((completeCount / visibleBlocks.length) * 100) : 0;
        const newStatus: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' =
          completeCount === 0 ? 'NOT_STARTED'
          : completeCount === visibleBlocks.length ? 'COMPLETED'
          : 'IN_PROGRESS';
        await DataService.updatePathwayProgress(user.id, pathwayId, {
          progress,
          status: newStatus,
          lastActivity: Date.now(),
        });
      } catch (e) {
        console.error('Failed to load pathway', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user.id, pathwayId, selectedTrack]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aero-600"></div>
      </div>
    );
  }

  if (!pathway) {
    return (
      <div className="p-8 text-center text-aero-400 italic">
        {t('candidate.pathway.notFound', 'Pathway not found.')}
      </div>
    );
  }

  const handleSubmitLevel = async (groupKey: string) => {
    const entry: LevelReviewEntry = { status: 'submitted', submittedAt: Date.now() };
    const next = { ...levelReviews, [groupKey]: entry };
    await DataService.updatePathwayProgress(user.id, pathwayId, { levelReviews: next });
    setLevelReviews(next);
  };

  return (
    <div className="overflow-y-auto">
      <PathwayProgressFlow
        pathway={pathway}
        blockProgressMap={blockProgressMap}
        selectedTrack={selectedTrack}
        onSelectBlock={onSelectBlock}
        levelReviews={levelReviews}
        onSubmitLevel={handleSubmitLevel}
      />
    </div>
  );
};
